import { Link, NavLink, Outlet } from 'react-router'
import { useTranslation } from 'react-i18next'
import { defaultHomeFor } from '@/auth/roles'
import { useSession } from '@/auth/SessionProvider'
import { Button } from '@/components/ui/button'
import { FenceAuthLogo } from '@/components/brand/FenceAuthLogo'

const NAV_LINKS = [
  { to: '/#platform', label: 'Platform' },
  { to: '/pricing', label: 'Pricing' },
]

const FOOTER_COLUMNS: { title: string; links: string[] }[] = [
  {
    title: 'Platform',
    links: ['Identity & SSO', 'Subscriptions', 'Entitlements', 'Hosted portal', 'Webhooks'],
  },
  {
    title: 'Developers',
    links: ['Documentation', 'API reference', 'Changelog', 'Status', 'Sandbox'],
  },
  { title: 'Company', links: ['About', 'Customers', 'Careers', 'Blog', 'Contact'] },
  { title: 'Legal', links: ['Trust centre', 'Privacy', 'Terms', 'DPA', 'Sub-processors'] },
]

export function PublicLayout() {
  const { t } = useTranslation()
  // The marketing site is anonymous-first, but a signed-in visitor landing
  // back here (post-login redirect, a bookmark, "Sign in" clicked again)
  // must see their own state — not a Sign in button that just re-enters the
  // already-authenticated login flow. Render nothing here while the session
  // bootstrap is in flight so the correct state appears once, not twice.
  const { user, isLoading } = useSession()

  return (
    <div className="flex min-h-dvh flex-col bg-surface-raised">
      <header className="sticky top-0 z-40 border-b bg-surface-raised/90 backdrop-blur">
        <div className="mx-auto flex h-[64px] w-full max-w-6xl items-center gap-7 px-5">
          <Link to="/" aria-label="FenceAuth home">
            <FenceAuthLogo className="h-7" />
          </Link>
          <nav className="hidden items-center gap-5 text-[13.5px] font-medium text-content-body sm:flex">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  isActive && !link.to.includes('#')
                    ? 'border-b-2 border-brand pb-0.5 font-semibold text-content'
                    : 'transition-colors hover:text-content'
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a href="/docs" className="transition-colors hover:text-content">
              {t('common.docs')}
            </a>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {isLoading ? null : user ? (
              <Button size="lg" asChild>
                <Link to={defaultHomeFor(user)}>Open dashboard</Link>
              </Button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-[13.5px] font-medium text-content-body hover:text-content"
                >
                  {t('common.signIn')}
                </Link>
                <Button size="lg" asChild>
                  <Link to="/signup">Start free</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="bg-console px-5 pt-11 pb-7 sm:px-10">
        <div className="mx-auto w-full max-w-6xl">
          <div className="grid grid-cols-2 gap-7 border-b border-white/10 pb-8 md:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]">
            <div className="col-span-2 md:col-span-1">
              <FenceAuthLogo onDark className="mb-3.5 h-7" />
              <p className="max-w-[250px] text-xs leading-relaxed text-sidebar-muted">
                Auth and billing infrastructure for SaaS. Built in the open, run for teams who'd
                rather ship product.
              </p>
              <p className="mt-3.5 flex items-center gap-2 font-mono text-[11.5px] text-[#7C8CA6]">
                <span className="size-1.5 rounded-full bg-accent" aria-hidden />
                All systems operational
              </p>
            </div>
            {FOOTER_COLUMNS.map((column) => (
              <div key={column.title}>
                <p className="mb-3.5 font-mono text-[10.5px] font-semibold tracking-widest text-[#7C8CA6] uppercase">
                  {column.title}
                </p>
                <ul className="flex flex-col gap-2.5 text-xs text-sidebar-fg">
                  {column.links.map((label) => (
                    <li key={label}>
                      <a href="/docs" className="hover:text-white">
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-5 font-mono text-[11.5px] text-[#7C8CA6]">
            <span>© {new Date().getFullYear()} FenceAuth Ltd</span>
            <span>SOC 2 Type II</span>
            <span>GDPR ready</span>
            <span className="ml-auto">api v2 · 256 endpoints</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
